import { Button, Callout, FormField, TextInput } from "@moneymap/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./auth.css";
import { useAuth } from "./AuthProvider";

type Credentials = {
  email: string;
  password: string;
};

function AuthLayout({
  title,
  description,
  children,
  variant = "standard",
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  variant?: "standard" | "orchid";
}) {
  if (variant === "orchid") {
    return (
      <main className="auth-shell--orchid">
        <section className="auth-orchid-hero" aria-label="IterNest Money introduction">
          <Link className="auth-orchid-brand" to="/" aria-label="IterNest Money home">
            <span className="auth-orchid-brand__mark" aria-hidden="true">
              I
            </span>
            <span className="auth-orchid-brand__name">
              <span>IterNest</span>
              <small>Money</small>
            </span>
          </Link>

          <div className="auth-orchid-copy">
            <p className="auth-orchid-copy__eyebrow">Household financial clarity</p>
            <p className="auth-orchid-copy__promise">
              Protect first.
              <br />
              Improve second.
            </p>
            <p className="auth-orchid-copy__caption">
              See what is funded, what needs attention, and what to do next.
            </p>
          </div>

          <img className="auth-orchid-art" src="/orchid.svg" alt="" aria-hidden="true" />
        </section>

        <section className="auth-orchid-panel">
          <div className="auth-orchid-card">
            <p className="auth-orchid-card__eyebrow">Private household workspace</p>
            <h1>{title}</h1>
            <p className="auth-orchid-card__description">{description}</p>
            {children}
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-shell">
      <section className="auth-brand" aria-label="IterNest Money introduction">
        <Link className="brand-mark brand-mark--auth" to="/" aria-label="IterNest Money home">
          <span className="brand-mark__map" aria-hidden="true">
            I
          </span>
          <span>IterNest Money</span>
        </Link>
        <p className="auth-brand__promise">
          Understand what is covered, what needs attention, and what to do next.
        </p>
        <p className="auth-brand__note">
          IterNest Money organizes your household plan. It does not move money or provide legal,
          tax, or investment advice.
        </p>
      </section>
      <section className="auth-panel">
        <div className="auth-panel__inner">
          <p className="mm-eyebrow">Private household workspace</p>
          <h1>{title}</h1>
          <p>{description}</p>
          {children}
        </div>
      </section>
    </main>
  );
}

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string>();
  const { register, handleSubmit, formState } = useForm<Credentials>();

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setSubmitError(undefined);
    try {
      await signIn(email, password);
      const requested = (location.state as { from?: string } | null)?.from;
      navigate(requested ?? "/", { replace: true });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Sign in could not be completed.");
    }
  });

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to open your current household briefing."
      variant="orchid"
    >
      {submitError ? (
        <Callout title="Sign in did not work" tone="urgent" role="alert">
          {submitError}
        </Callout>
      ) : null}
      <form className="form-stack" onSubmit={onSubmit}>
        <FormField label="Email" htmlFor="login-email" error={formState.errors.email?.message}>
          <TextInput
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register("email", { required: "Enter your email address." })}
          />
        </FormField>
        <FormField
          label="Password"
          htmlFor="login-password"
          error={formState.errors.password?.message}
        >
          <TextInput
            id="login-password"
            type="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            {...register("password", { required: "Enter your password." })}
          />
        </FormField>
        <Button type="submit" isLoading={formState.isSubmitting}>
          Sign in
        </Button>
      </form>
      <div className="auth-orchid-links">
        <Link to="/forgot-password">Reset password</Link>
      </div>
      <p className="auth-orchid-create">
        New to IterNest Money? <Link to="/signup">Create an account</Link>
      </p>
      <p className="auth-orchid-trust">
        <span className="auth-orchid-trust__icon" aria-hidden="true">
          ✓
        </span>
        <span>Your financial data stays private. IterNest Money never moves money.</span>
      </p>
    </AuthLayout>
  );
}

export function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const { register, handleSubmit, watch, formState } = useForm<
    Credentials & { confirmPassword: string; privacy: boolean }
  >();
  const password = watch("password");

  const onSubmit = handleSubmit(async ({ email, password: submittedPassword }) => {
    setSubmitError(undefined);
    try {
      const result = await signUp(email, submittedPassword);
      if (result.requiresEmailConfirmation) {
        setMessage("Check your email to confirm the account. Then return here to sign in.");
      } else {
        navigate("/onboarding/profile", { replace: true });
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Account creation could not be completed.",
      );
    }
  });

  return (
    <AuthLayout
      title="Create your IterNest Money account"
      description="Start with the facts needed for one trustworthy briefing."
    >
      {message ? (
        <Callout title="One more step" tone="positive" role="status">
          {message}
        </Callout>
      ) : null}
      {submitError ? (
        <Callout title="Account not created" tone="urgent" role="alert">
          {submitError}
        </Callout>
      ) : null}
      <form className="form-stack" onSubmit={onSubmit}>
        <FormField label="Email" htmlFor="signup-email" error={formState.errors.email?.message}>
          <TextInput
            id="signup-email"
            type="email"
            autoComplete="email"
            {...register("email", { required: "Enter your email address." })}
          />
        </FormField>
        <FormField
          label="Password"
          htmlFor="signup-password"
          hint="Use at least 12 characters."
          error={formState.errors.password?.message}
        >
          <TextInput
            id="signup-password"
            type="password"
            autoComplete="new-password"
            {...register("password", {
              required: "Create a password.",
              minLength: { value: 12, message: "Use at least 12 characters." },
            })}
          />
        </FormField>
        <FormField
          label="Confirm password"
          htmlFor="signup-confirm"
          error={formState.errors.confirmPassword?.message}
        >
          <TextInput
            id="signup-confirm"
            type="password"
            autoComplete="new-password"
            {...register("confirmPassword", {
              validate: (value) => value === password || "Passwords must match.",
            })}
          />
        </FormField>
        <label className="check-row">
          <input
            type="checkbox"
            {...register("privacy", { required: "Review and accept the privacy notice." })}
          />
          <span>
            I understand IterNest Money stores the financial information I enter to calculate my
            household plan.
          </span>
        </label>
        {formState.errors.privacy ? (
          <p className="field-error">{formState.errors.privacy.message}</p>
        ) : null}
        <Button type="submit" isLoading={formState.isSubmitting}>
          Create secure account
        </Button>
      </form>
      <div className="auth-links">
        <span>
          Already have an account? <Link to="/login">Sign in</Link>
        </span>
      </div>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const { sendPasswordReset } = useAuth();
  const [message, setMessage] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const { register, handleSubmit, formState } = useForm<{ email: string }>();

  const onSubmit = handleSubmit(async ({ email }) => {
    setSubmitError(undefined);
    try {
      await sendPasswordReset(email);
      setMessage("If that email belongs to an IterNest Money account, a reset link is on its way.");
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "The reset request could not be completed.",
      );
    }
  });

  return (
    <AuthLayout title="Reset your password" description="We will send a secure reset link.">
      {message ? (
        <Callout title="Check your email" tone="positive" role="status">
          {message}
        </Callout>
      ) : null}
      {submitError ? (
        <Callout title="Reset not sent" tone="urgent" role="alert">
          {submitError}
        </Callout>
      ) : null}
      <form className="form-stack" onSubmit={onSubmit}>
        <FormField label="Email" htmlFor="reset-email">
          <TextInput
            id="reset-email"
            type="email"
            autoComplete="email"
            {...register("email", { required: true })}
          />
        </FormField>
        <Button type="submit" isLoading={formState.isSubmitting}>
          Send reset link
        </Button>
      </form>
      <div className="auth-links">
        <Link to="/login">Back to sign in</Link>
      </div>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string>();
  const [isUpdated, setIsUpdated] = useState(false);
  const { register, handleSubmit, watch, formState } = useForm<{
    password: string;
    confirmPassword: string;
  }>();
  const password = watch("password");
  const onSubmit = handleSubmit(async ({ password: nextPassword }) => {
    setSubmitError(undefined);
    try {
      await updatePassword(nextPassword);
      setIsUpdated(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "The password could not be updated.");
    }
  });
  return (
    <AuthLayout
      title="Choose a new password"
      description="Your reset link opens a short-lived recovery session."
    >
      {isUpdated ? (
        <Callout title="Password updated" tone="positive" role="status">
          <Button type="button" onClick={() => navigate("/")}>
            Open IterNest Money
          </Button>
        </Callout>
      ) : (
        <form className="form-stack" onSubmit={onSubmit}>
          <FormField
            label="New password"
            htmlFor="new-password"
            error={formState.errors.password?.message}
          >
            <TextInput
              id="new-password"
              type="password"
              autoComplete="new-password"
              {...register("password", {
                required: "Enter a new password.",
                minLength: { value: 12, message: "Use at least 12 characters." },
              })}
            />
          </FormField>
          <FormField
            label="Confirm new password"
            htmlFor="confirm-new-password"
            error={formState.errors.confirmPassword?.message}
          >
            <TextInput
              id="confirm-new-password"
              type="password"
              autoComplete="new-password"
              {...register("confirmPassword", {
                validate: (value) => value === password || "Passwords must match.",
              })}
            />
          </FormField>
          {submitError ? (
            <Callout title="Password not updated" tone="urgent" role="alert">
              {submitError}
            </Callout>
          ) : null}
          <Button type="submit" isLoading={formState.isSubmitting}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
